// Import Dependencies
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  TrashIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  VideoCameraIcon,
  DocumentIcon,
  ScissorsIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import PropTypes from "prop-types";

// Local Imports
import { Button, Textarea } from "components/ui";
import { toast } from "sonner";

// ----------------------------------------------------------------------

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, originalType = 'image/jpeg') {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Calculate rotated dimensions
  const rotRad = (rotation * Math.PI) / 180;
  const rotatedWidth =
    Math.abs(Math.cos(rotRad) * image.width) +
    Math.abs(Math.sin(rotRad) * image.height);
  const rotatedHeight =
    Math.abs(Math.sin(rotRad) * image.width) +
    Math.abs(Math.cos(rotRad) * image.height);

  // Set canvas size to rotated image size
  canvas.width = rotatedWidth;
  canvas.height = rotatedHeight;

  // Rotate and draw the full image
  ctx.save();
  ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);
  ctx.restore();

  // Now crop from the rotated image
  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    // WhatsApp accepts: image/jpeg, image/png, image/webp
    // Use original type if supported, otherwise default to jpeg
    const supportedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const outputType = supportedImageTypes.includes(originalType) ? originalType : 'image/jpeg';
    const quality = outputType === 'image/jpeg' ? 0.95 : 0.92;
    
    croppedCanvas.toBlob((blob) => {
      resolve(blob);
    }, outputType, quality);
  });
}

// Helper function to trim video
function getTrimmedVideo(videoElement, startTime, endTime) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      // Set canvas dimensions to match video
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      // Select a WhatsApp-compatible MIME type that the browser supports
      // WhatsApp accepts: video/mp4, video/3gpp
      // NOTE: WebM is NOT supported by WhatsApp, so we only use mp4
      let mimeType = "video/mp4"; // Only WhatsApp-compatible format
      const supportedTypes = [
        "video/mp4",
        "video/mp4;codecs=h264",
        "video/mp4;codecs=avc1"
        // Removed all webm variants - not supported by WhatsApp
      ];
      
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Create MediaRecorder to capture the trimmed video
      const stream = canvas.captureStream();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        videoBitsPerSecond: 2500000,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Determine actual recorded MIME type from the first chunk
        const actualType = chunks[0]?.type || mimeType;
        // If browser produced WebM, reject as unsupported for WhatsApp to avoid silent failures later
        if (actualType && actualType.includes('webm')) {
          reject(new Error('UNSUPPORTED_OUTPUT_WEBM'));
          return;
        }
        // Normalize mp4 type (strip codec parameters)
        const normalizedType = actualType && actualType.startsWith('video/mp4') ? 'video/mp4' : mimeType;
        const blob = new Blob(chunks, { type: normalizedType });
        resolve(blob);
      };

      mediaRecorder.onerror = (e) => {
        reject(e);
      };

      // Start recording
      mediaRecorder.start();

      // Set video to start time and wait for seek
      videoElement.currentTime = startTime;
      videoElement.onseeked = () => {
        // Play video and draw frames to canvas
        videoElement.play();
        
        const drawFrame = () => {
          if (videoElement.currentTime >= endTime) {
            videoElement.pause();
            mediaRecorder.stop();
            return;
          }
          
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          requestAnimationFrame(drawFrame);
        };

        drawFrame();
      };
    } catch (error) {
      reject(error);
    }
  });
}

export function MediaPreviewModal({
  isOpen,
  onClose,
  files,
  onConfirm,
  initialIndex = 0,
  isUploading = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [caption, setCaption] = useState("");
  const [localFiles, setLocalFiles] = useState([]);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rotation, setRotation] = useState(0);
  
  // Video trimming states
  const [isTrimming, setIsTrimming] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimVideoUrl, setTrimVideoUrl] = useState(null); // URL for original video when trimming
  const videoRef = useRef(null);

  useEffect(() => {
    if (files && files.length > 0) {
      // Only initialize if localFiles is empty or file count changed
      // This prevents losing files during crop/edit operations
      if (localFiles.length === 0 || localFiles.length !== files.length) {
        const mappedFiles = files.map((file) => {
          // Preserve the originalFile reference - this is crucial for multiple uploads
          const originalFile = file.originalFile || file.file || file
          
          // Determine which file to use for preview
          // For editing: use the current working file (trimmed/cropped if exists)
          // The file passed from parent is already the correct one
          let fileToUse = file.file || file.originalFile || file
          let previewUrl = file.preview
          
          // For videos and images, always regenerate preview URL from the actual file object
          // This ensures the preview is valid even after editing
          if (fileToUse && (fileToUse instanceof Blob || fileToUse instanceof File)) {
            const fileType = fileToUse.type || file.type || ''
            if (fileType.startsWith('video/') || fileType.startsWith('image/')) {
              // Always create a fresh preview URL to ensure it's valid
              previewUrl = URL.createObjectURL(fileToUse)
            }
          }
          
          return {
            ...file,
            caption: file.caption || "",
            originalFile: originalFile, // Always preserve original file reference
            file: fileToUse, // Current working file
            preview: previewUrl, // Use the valid preview URL
            croppedFile: file.croppedFile || null,
            trimmedFile: file.trimmedFile || null,
            originalPreview: file.originalPreview || previewUrl, // Store original preview for cropping/trimming
            lastCropState: file.lastCropState || null, // Store last crop state (zoom, rotation, crop)
            lastTrimState: file.lastTrimState || null, // Store last trim state (start, end, duration)
            trimStart: file.trimStart || 0, // Store video trim start
            trimEnd: file.trimEnd || 0, // Store video trim end
            trimDuration: file.trimDuration || 0, // Store video trim duration
          }
        });
        
        setLocalFiles(mappedFiles);
        setCurrentIndex(initialIndex);
        setCaption(files[initialIndex]?.caption || "");
        setIsCropping(false);
        setIsTrimming(false);
        
        // Don't reset crop state here - it will be set when entering crop mode
      }
    }
  }, [files, initialIndex, isOpen, localFiles.length]);

  const currentFile = localFiles[currentIndex];

  // IMPORTANT: Do not revoke preview URLs on every localFiles change.
  // That caused previews for other files to break after cropping one file.
  // We rely on explicit cleanup in handleClose/handleConfirm.
  useEffect(() => {
    return () => {
      // No-op cleanup here to avoid revoking previews still in use.
    };
  }, []);

  // Restore crop state when file changes
  useEffect(() => {
    if (currentFile) {
      // Restore crop state if it exists
      if (currentFile.lastCropState) {
        setCrop(currentFile.lastCropState.crop);
        setZoom(currentFile.lastCropState.zoom);
        setRotation(currentFile.lastCropState.rotation);
      } else {
        // Reset to defaults for new images
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
      }
      
      // Restore trim state for videos
      if (currentFile.type?.startsWith('video/')) {
        if (currentFile.lastTrimState) {
          setTrimStart(currentFile.lastTrimState.trimStart);
          setTrimEnd(currentFile.lastTrimState.trimEnd);
        }
        // If video duration is already known from previous state, use it
        if (currentFile.lastTrimState?.trimEnd) {
          // Don't override videoDuration here - let video metadata load naturally
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      if (!currentFile) {
        toast.error("No file selected for cropping.");
        return;
      }
      
      if (!currentFile.preview) {
        toast.error("Image preview not available. Please try selecting the file again.");
        return;
      }
      
      if (!croppedAreaPixels) {
        toast.error("Unable to crop image. Please adjust the crop area.");
        return;
      }

      toast.loading("Cropping image...", { id: "crop-loading" });

      // Always crop from the original preview, not the previously cropped one
      const imageToUse = currentFile.originalPreview || currentFile.preview;
      
      // Get original file type for WhatsApp compatibility
      const originalFileObj = currentFile.originalFile || currentFile.file;
      const originalType = originalFileObj?.type || currentFile.type || 'image/jpeg';
      
      const croppedBlob = await getCroppedImg(
        imageToUse,
        croppedAreaPixels,
        rotation,
        originalType
      );

      if (!croppedBlob) {
        toast.error("Failed to crop image. Please try again.", { id: "crop-loading" });
        return;
      }

      const croppedUrl = URL.createObjectURL(croppedBlob);

      // Clean up old cropped preview URL if it's different from original
      if (localFiles[currentIndex]?.preview && 
          localFiles[currentIndex].preview !== localFiles[currentIndex].originalPreview) {
        URL.revokeObjectURL(localFiles[currentIndex].preview);
      }

      const updatedFiles = [...localFiles];
      const currentFileData = updatedFiles[currentIndex];
      
      updatedFiles[currentIndex] = {
        ...currentFileData,
        file: croppedBlob,
        croppedFile: croppedBlob,
        originalFile: currentFileData.originalFile || currentFileData.file, // IMPORTANT: Preserve original file
        preview: croppedUrl, // This is the display preview
        originalPreview: currentFileData.originalPreview || currentFileData.preview, // Preserve original
        lastCropState: { // Save the current crop state for next time
          crop: crop,
          zoom: zoom,
          rotation: rotation,
        },
        name: currentFileData.name.includes('cropped_') 
          ? currentFileData.name 
          : `cropped_${currentFileData.name}`,
        size: croppedBlob.size,
      };

      setLocalFiles(updatedFiles);
      setIsCropping(false);
      
      // Keep the crop state so it's preserved for next edit
      // Don't reset: setCrop, setZoom, setRotation
      
      toast.success("Image cropped successfully!", { id: "crop-loading" });
    } catch (e) {
      console.error("Error cropping image:", e);
      toast.error("Failed to crop image. Please try again.", { id: "crop-loading" });
    }
  };

  const handleNext = () => {
    if (currentIndex < localFiles.length - 1) {
      // Save current caption
      const updated = [...localFiles];
      updated[currentIndex].caption = caption;
      setLocalFiles(updated);
      
      setCurrentIndex(currentIndex + 1);
      setCaption(localFiles[currentIndex + 1]?.caption || "");
      setIsCropping(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      // Save current caption
      const updated = [...localFiles];
      updated[currentIndex].caption = caption;
      setLocalFiles(updated);
      
      setCurrentIndex(currentIndex - 1);
      setCaption(localFiles[currentIndex - 1]?.caption || "");
      setIsCropping(false);
    }
  };

  const handleRemove = () => {
    // Clean up preview URL for the file being removed
    const fileToRemove = localFiles[currentIndex];
    if (fileToRemove?.preview && fileToRemove.preview !== fileToRemove.originalPreview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    const updated = localFiles.filter((_, idx) => idx !== currentIndex);
    setLocalFiles(updated);

    if (updated.length === 0) {
      // When all files are deleted, confirm with empty array and close
      onConfirm([]);
      onClose();
    } else if (currentIndex >= updated.length) {
      setCurrentIndex(updated.length - 1);
      setCaption(updated[updated.length - 1]?.caption || "");
      setIsCropping(false);
      setIsTrimming(false);
    } else {
      setCaption(updated[currentIndex]?.caption || "");
      setIsCropping(false);
      setIsTrimming(false);
    }
  };

  const handleConfirm = () => {
    // Save current caption
    const updated = [...localFiles];
    updated[currentIndex].caption = caption;
    
    // Don't clean up preview URLs when confirming - parent will handle them
    // Clean up trim video URL
    if (trimVideoUrl && trimVideoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(trimVideoUrl);
    }
    
    // Reset all states
    setLocalFiles([]);
    setCurrentIndex(0);
    setCaption("");
    setIsCropping(false);
    setIsTrimming(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setVideoDuration(0);
    setTrimStart(0);
    setTrimEnd(0);
    setCurrentTime(0);
    setTrimVideoUrl(null);
    
    onConfirm(updated);
    onClose();
  };

  const handleClose = () => {
    // Clean up preview URLs when canceling (but not original previews as they might be reused)
    localFiles.forEach(file => {
      // Only revoke if it's a cropped preview (different from original)
      if (file.preview && file.preview !== file.originalPreview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    
    // Clean up trim video URL
    if (trimVideoUrl && trimVideoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(trimVideoUrl);
    }
    
    // Reset all states
    setLocalFiles([]);
    setCurrentIndex(0);
    setCaption("");
    setIsCropping(false);
    setIsTrimming(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setVideoDuration(0);
    setTrimStart(0);
    setTrimEnd(0);
    setCurrentTime(0);
    setTrimVideoUrl(null);
    
    onClose();
  };

  // Video handlers
  const handleVideoLoaded = (e) => {
    const duration = e.target.duration;
    setVideoDuration(duration);
    
    // When in trim mode, always check for lastTrimState to restore previous trim times
    if (isTrimming) {
      if (currentFile?.lastTrimState) {
        // Restore previous trim times
        const savedStart = currentFile.lastTrimState.trimStart;
        const savedEnd = currentFile.lastTrimState.trimEnd;
        
        // Ensure the saved times are within the video duration
        setTrimStart(Math.min(savedStart, duration));
        setTrimEnd(Math.min(savedEnd, duration));
        
        // Set video to the trim start position
        if (videoRef.current) {
          videoRef.current.currentTime = Math.min(savedStart, duration);
        }
        setCurrentTime(Math.min(savedStart, duration));
      } else {
        // First time trimming this video - use full duration
        setTrimStart(0);
        setTrimEnd(duration);
        setCurrentTime(0);
      }
    } else {
      // Not in trim mode - just viewing the video
      // If video has trim state, we can still set it for when user enters trim mode
      if (currentFile?.lastTrimState) {
        setTrimStart(currentFile.lastTrimState.trimStart);
        setTrimEnd(currentFile.lastTrimState.trimEnd);
      } else {
        setTrimStart(0);
        setTrimEnd(duration);
      }
      setCurrentTime(0);
    }
  };

  const handleTimeUpdate = (e) => {
    const time = e.target.currentTime;
    setCurrentTime(time);
    
    // Loop playback between trim points when trimming
    if (isTrimming && time >= trimEnd) {
      e.target.currentTime = trimStart;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleTrimVideo = async () => {
    try {
      if (!videoRef.current) {
        toast.error("Video not loaded. Please try again.");
        return;
      }

      if (trimStart >= trimEnd) {
        toast.error("Invalid trim range. Start time must be before end time.");
        return;
      }

      toast.loading("Trimming video...", { id: "trim-loading" });

      // Trim the video
      const trimmedBlob = await getTrimmedVideo(videoRef.current, trimStart, trimEnd);

      if (!trimmedBlob) {
        toast.error("Failed to trim video. Please try again.", { id: "trim-loading" });
        return;
      }

      const trimmedUrl = URL.createObjectURL(trimmedBlob);

      // Clean up old trimmed preview URL if it's different from original
      if (localFiles[currentIndex]?.preview && 
          localFiles[currentIndex].preview !== localFiles[currentIndex].originalPreview) {
        URL.revokeObjectURL(localFiles[currentIndex].preview);
      }

      const updated = [...localFiles];
      const currentFileData = updated[currentIndex];
      
      // Ensure the trimmed video has .mp4 extension
      let trimmedFileName = currentFileData.name.includes('trimmed_') 
        ? currentFileData.name 
        : `trimmed_${currentFileData.name}`;
      
      // Replace extension with .mp4 if it's not already
      if (!trimmedFileName.toLowerCase().endsWith('.mp4')) {
        trimmedFileName = trimmedFileName.replace(/\.[^.]+$/, '') + '.mp4';
      }
      
      updated[currentIndex] = {
        ...currentFileData,
        file: trimmedBlob,
        trimmedFile: trimmedBlob,
        originalFile: currentFileData.originalFile || currentFileData.file, // IMPORTANT: Preserve original file
        preview: trimmedUrl,
        originalPreview: currentFileData.originalPreview || currentFileData.preview,
        lastTrimState: { // Save the trim state for next time
          trimStart,
          trimEnd,
          trimDuration: trimEnd - trimStart,
        },
        trimStart,
        trimEnd,
        trimDuration: trimEnd - trimStart,
        name: trimmedFileName,
        size: trimmedBlob.size,
        type: 'video/mp4', // Ensure type is set to mp4
      };

      setLocalFiles(updated);
      setIsTrimming(false);
      
      // Clean up trim video URL
      if (trimVideoUrl && trimVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(trimVideoUrl);
      }
      setTrimVideoUrl(null);
      
      toast.success("Video trimmed successfully!", { id: "trim-loading" });
    } catch (e) {
      console.error("Error trimming video:", e);
      if (e && e.message === 'UNSUPPORTED_OUTPUT_WEBM') {
        toast.error("Your browser produced a WebM video which WhatsApp doesn't support. Please use a browser that supports MP4 for trimming (e.g., Safari) or send the original video.", { id: "trim-loading" });
      } else {
        toast.error("Failed to trim video. Please try again.", { id: "trim-loading" });
      }
      
      // Clean up trim video URL on error too
      if (trimVideoUrl && trimVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(trimVideoUrl);
      }
      setTrimVideoUrl(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const isImage = currentFile?.type?.startsWith("image/");
  const isVideo = currentFile?.type?.startsWith("video/");
  const isPDF = currentFile?.type === "application/pdf";

  // Don't render if no files
  if (!localFiles || localFiles.length === 0) {
    return null;
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[99998] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity dark:bg-black/40" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="scrollbar-sm relative flex w-full max-w-5xl h-[90vh] flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4 bg-gradient-to-r from-[#075E54] to-[#128C7E] dark:from-[#075E54] dark:to-[#0a7a6f] dark:border-gray-700 sm:px-5">
              <div className="flex items-center gap-3">
                {!isCropping ? (
                  <button
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                ) : (
                  <div className="w-9 h-9" />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {isCropping ? "Crop Image" : isTrimming ? "Trim Video" : "Preview & Add Caption"}
                  </h3>
                  <p className="text-sm text-white/90">
                    {isCropping 
                      ? "Adjust zoom and rotation to crop the image" 
                      : isTrimming 
                      ? "Select start and end points to trim the video" 
                      : `${localFiles.length} file${localFiles.length > 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!isCropping && !isTrimming && isImage && (
                  <button
                    onClick={() => {
                      // Check if we have a valid preview
                      if (!currentFile?.originalPreview && !currentFile?.preview) {
                        toast.error("Image preview not available. Please try again.");
                        return;
                      }
                      
                      // Restore last crop state if it exists, otherwise start fresh
                      if (currentFile?.lastCropState) {
                        setCrop(currentFile.lastCropState.crop);
                        setZoom(currentFile.lastCropState.zoom);
                        setRotation(currentFile.lastCropState.rotation);
                      } else {
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                        setRotation(0);
                      }
                      setCroppedAreaPixels(null);
                      setIsCropping(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-white/20 hover:bg-white/30 px-3 py-2 text-sm font-medium text-white transition-colors backdrop-blur-sm"
                    title="Crop Image"
                  >
                    <ScissorsIcon className="size-4" />
                    <span>Crop</span>
                  </button>
                )}
                {!isCropping && !isTrimming && isVideo && videoDuration > 0 && (
                  <button
                    onClick={() => {
                      // When trimming, we need to show the ORIGINAL full-length video
                      // Create URL from originalFile if not already available
                      const originalFile = currentFile?.originalFile;
                      if (originalFile && (originalFile instanceof Blob || originalFile instanceof File)) {
                        // Create preview URL from original file
                        const originalUrl = URL.createObjectURL(originalFile);
                        setTrimVideoUrl(originalUrl);
                      } else {
                        // Fallback to originalPreview if available
                        setTrimVideoUrl(currentFile?.originalPreview || currentFile?.preview);
                      }
                      
                      // Enter trim mode - let handleVideoLoaded set the trim times
                      // when the original video loads with correct duration
                      setIsTrimming(true);
                      
                      // Reset video duration so handleVideoLoaded will recalculate
                      setVideoDuration(0);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-white/20 hover:bg-white/30 px-3 py-2 text-sm font-medium text-white transition-colors backdrop-blur-sm"
                    title="Trim Video"
                  >
                    <ScissorsIcon className="size-4" />
                    <span>Trim</span>
                  </button>
                )}
                {!isCropping && !isTrimming && (
                  <button
                    onClick={handleRemove}
                    className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                    title="Remove this file"
                  >
                    <TrashIcon className="size-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Media Preview Area */}
            <div className="relative flex-1 bg-gray-100 dark:bg-gray-800 overflow-hidden">
              {isCropping && isImage && (currentFile?.originalPreview || currentFile?.preview) ? (
                <div className="absolute inset-0 w-full h-full bg-gray-900">
                  <div className="relative w-full h-full">
                    <Cropper
                      image={currentFile.originalPreview || currentFile.preview}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={4 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onRotationChange={setRotation}
                      onCropComplete={onCropComplete}
                      objectFit="contain"
                      showGrid={true}
                      zoomSpeed={0.1}
                      restrictPosition={false}
                      style={{
                        containerStyle: {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: '#1f2937'
                        },
                        mediaStyle: {},
                        cropAreaStyle: {
                          border: '3px solid #25D366',
                          boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.5)',
                        }
                      }}
                    />
                  </div>
                </div>
              ) : isCropping && isImage && !(currentFile?.originalPreview || currentFile?.preview) ? (
                <div className="absolute inset-0 w-full h-full bg-gray-900 flex items-center justify-center">
                  <div className="text-center text-white">
                    <p className="text-lg font-medium mb-2">Image preview not available</p>
                    <p className="text-sm opacity-75">Please close and try again</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full h-full flex items-center justify-center p-6">
                    {isImage && currentFile && (
                      <img
                        src={currentFile.preview}
                        alt={currentFile.name}
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    )}
                    {isVideo && currentFile && (
                      <video
                        key={isTrimming ? (trimVideoUrl || currentFile.preview) : currentFile.preview} // Force re-render when preview URL changes
                        ref={videoRef}
                        src={isTrimming ? (trimVideoUrl || currentFile.preview) : currentFile.preview} // Use original video when trimming
                        controls
                        onLoadedMetadata={handleVideoLoaded}
                        onTimeUpdate={handleTimeUpdate}
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    )}
                    {!isImage && !isVideo && currentFile && (
                      <div className="flex flex-col items-center gap-4">
                        {isPDF ? (
                          <DocumentTextIcon className="size-20 text-red-500" />
                        ) : (
                          <DocumentIcon className="size-20 text-gray-600 dark:text-gray-300 opacity-80" />
                        )}
                        <p className="text-base font-medium text-gray-900 dark:text-white">{currentFile.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatFileSize(currentFile.size)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Navigation Arrows */}
                  {localFiles.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-700/90 rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="size-6 text-gray-800 dark:text-white" />
                      </button>
                      <button
                        onClick={handleNext}
                        disabled={currentIndex === localFiles.length - 1}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-700/90 rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="size-6 text-gray-800 dark:text-white" />
                      </button>

                      {/* Counter */}
                      <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                        {currentIndex + 1} / {localFiles.length}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Thumbnail Strip */}
            {localFiles.length > 1 && (
              <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-3">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {localFiles.map((file, index) => (
                    <button
                      key={file.id || index}
                      onClick={() => {
                        // Save current caption
                        const updated = [...localFiles];
                        updated[currentIndex].caption = caption;
                        setLocalFiles(updated);
                        setCurrentIndex(index);
                        setCaption(updated[index].caption || "");
                        // Exit crop/trim mode when switching files
                        setIsCropping(false);
                        setIsTrimming(false);
                      }}
                      className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentIndex
                          ? "border-[#25D366] ring-2 ring-[#25D366]/50"
                          : "border-gray-300 dark:border-gray-600 opacity-60 hover:opacity-100"
                      }`}
                    >
                      {file.type?.startsWith("image/") ? (
                        <img src={file.preview} alt="" className="w-full h-full object-cover" />
                      ) : file.type?.startsWith("video/") ? (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <VideoCameraIcon className="size-6 text-white" />
                        </div>
                      ) : file.type === "application/pdf" ? (
                        <div className="w-full h-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                          <DocumentTextIcon className="size-6 text-red-500" />
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <DocumentIcon className="size-6 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Crop Controls Footer */}
            {isCropping && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
                <div className="max-w-3xl mx-auto space-y-5">
                  <div className="grid grid-cols-2 gap-8">
                    {/* Zoom Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Zoom
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[60px] px-3 py-1 text-sm font-bold text-[#075E54] bg-[#25D366]/20 dark:text-[#25D366] dark:bg-[#25D366]/20 rounded-full">
                            {zoom.toFixed(1)}x
                          </span>
                          <button
                            onClick={() => setZoom(1)}
                            className="text-xs font-medium text-gray-500 hover:text-[#128C7E] dark:text-gray-400 dark:hover:text-[#25D366] transition-colors underline"
                            title="Reset zoom"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="relative pt-1">
                        <input
                          type="range"
                          min={1}
                          max={3}
                          step={0.1}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full h-2.5 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#128C7E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:hover:bg-[#075E54] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
                          style={{
                            background: `linear-gradient(to right, #128C7E 0%, #128C7E ${((zoom - 1) / 2) * 100}%, #d1d5db ${((zoom - 1) / 2) * 100}%, #d1d5db 100%)`
                          }}
                        />
                      </div>
                    </div>

                    {/* Rotation Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Rotation
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[60px] px-3 py-1 text-sm font-bold text-[#075E54] bg-[#25D366]/20 dark:text-[#25D366] dark:bg-[#25D366]/20 rounded-full">
                            {rotation}°
                          </span>
                          <button
                            onClick={() => setRotation(0)}
                            className="text-xs font-medium text-gray-500 hover:text-[#128C7E] dark:text-gray-400 dark:hover:text-[#25D366] transition-colors underline"
                            title="Reset rotation"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="relative pt-1">
                        <input
                          type="range"
                          min={0}
                          max={360}
                          step={1}
                          value={rotation}
                          onChange={(e) => setRotation(Number(e.target.value))}
                          className="w-full h-2.5 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#128C7E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:hover:bg-[#075E54] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
                          style={{
                            background: `linear-gradient(to right, #128C7E 0%, #128C7E ${(rotation / 360) * 100}%, #d1d5db ${(rotation / 360) * 100}%, #d1d5db 100%)`
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setZoom(1);
                        setRotation(0);
                        setCrop({ x: 0, y: 0 });
                      }}
                      className="text-sm font-medium text-gray-500 hover:text-[#128C7E] dark:text-gray-400 dark:hover:text-[#25D366] transition-colors"
                    >
                      Reset All
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setIsCropping(false);
                          setCrop({ x: 0, y: 0 });
                          setZoom(1);
                          setRotation(0);
                        }}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-300 dark:border-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCropSave}
                        className="px-5 py-2.5 text-sm font-medium bg-[#128C7E] hover:bg-[#075E54] text-white rounded-lg transition-colors shadow-md"
                      >
                        Apply Crop
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trim Controls Footer - Simplified */}
            {isTrimming && isVideo && videoDuration > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
                <div className="max-w-3xl mx-auto space-y-4">
                  {/* Simple Timeline */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatTime(trimStart)} - {formatTime(trimEnd)}
                      </span>
                      <span className="font-semibold text-[#128C7E] dark:text-[#25D366]">
                        Duration: {formatTime(trimEnd - trimStart)}
                      </span>
                    </div>
                    
                    {/* Simple Progress Bar */}
                    <div className="relative h-2 bg-gray-300 dark:bg-gray-600 rounded-full">
                      <div
                        className="absolute h-full bg-[#128C7E] dark:bg-[#25D366] rounded-full"
                        style={{
                          left: `${(trimStart / videoDuration) * 100}%`,
                          right: `${100 - (trimEnd / videoDuration) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Simple Sliders */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 dark:text-gray-400">
                        Start: {formatTime(trimStart)}
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={videoDuration}
                        step={0.1}
                        value={trimStart}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value < trimEnd) {
                            setTrimStart(value);
                            if (videoRef.current) {
                              videoRef.current.currentTime = value;
                            }
                          }
                        }}
                        className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#128C7E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-[#075E54]"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 dark:text-gray-400">
                        End: {formatTime(trimEnd)}
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={videoDuration}
                        step={0.1}
                        value={trimEnd}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value > trimStart) {
                            setTrimEnd(value);
                            if (videoRef.current) {
                              videoRef.current.currentTime = value;
                            }
                          }
                        }}
                        className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#128C7E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-[#075E54]"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setIsTrimming(false);
                        setTrimStart(0);
                        setTrimEnd(videoDuration);
                        
                        // Clean up trim video URL
                        if (trimVideoUrl && trimVideoUrl.startsWith('blob:')) {
                          URL.revokeObjectURL(trimVideoUrl);
                        }
                        setTrimVideoUrl(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleTrimVideo}
                      className="px-4 py-2 text-sm font-medium bg-[#128C7E] hover:bg-[#075E54] text-white rounded-lg transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Caption Input Footer */}
            {!isCropping && !isTrimming && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-[#ECE5DD] dark:bg-gray-800">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Add a caption..."
                      className="min-h-[50px] max-h-[120px] resize-none bg-white dark:bg-gray-700 focus:border-[#25D366] focus:ring-[#25D366]"
                      rows={2}
                    />
                  </div>
                  <button
                    onClick={handleConfirm}
                    disabled={isUploading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#128C7E] hover:bg-[#075E54] text-white rounded-lg transition-colors shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <CheckIcon className="size-5" />
                        <span>Attach</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

MediaPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialIndex: PropTypes.number,
  isUploading: PropTypes.bool,
};

